import React from 'react'
import { TRANSITION_TYPES } from '../utils/transitionEngine'

/**
 * ImageEffectsPanel
 * -----------------
 * Image-specific controls: color correction/grading (item.colorGrade), a
 * cinematic camera-motion / Ken Burns picker (writes item.motion with the camera
 * presets), and a clip transition picker (item.transition). The generic
 * Animate / Motion / Effects buttons + panels (relaxed to include images)
 * handle the reused animation, motion and effect systems.
 */

const GRADE_PRESETS = [
  ['none', 'None'], ['cinematic', 'Cinematic'], ['warm', 'Warm'], ['cool', 'Cool'],
  ['blackWhite', 'Black & White'], ['sepia', 'Sepia'], ['vintage', 'Vintage'],
  ['faded', 'Faded Film'], ['matte', 'Matte'], ['vivid', 'Vivid'], ['hdr', 'HDR'],
  ['duotone', 'Duotone'], ['kodachrome', 'Kodachrome'], ['crossProcess', 'Cross Process'],
  ['lomo', 'Lomo'], ['filmNoir', 'Film Noir'], ['polaroid', 'Polaroid'],
]

const CAMERA_PRESETS = [
  ['', 'None'],
  ['kenBurnsZoomIn', 'Ken Burns — Zoom In'],
  ['kenBurnsZoomOut', 'Ken Burns — Zoom Out'],
  ['panLeft', 'Pan Left'], ['panRight', 'Pan Right'],
  ['tiltUp', 'Tilt Up'], ['tiltDown', 'Tilt Down'],
  ['dollyIn', 'Dolly In'], ['dollyOut', 'Dolly Out'],
  ['panAndZoom', 'Pan & Zoom'],
  ['cameraMove', 'Custom Camera Move'],
]

const TRANSITION_LABELS = {
  crossDissolve: 'Cross Dissolve', slideLeft: 'Slide Left', slideRight: 'Slide Right',
  slideUp: 'Slide Up', slideDown: 'Slide Down', wipeLeft: 'Wipe Left', wipeRight: 'Wipe Right',
  wipeUp: 'Wipe Up', wipeDown: 'Wipe Down', zoomIn: 'Zoom In', zoomOut: 'Zoom Out',
  push: 'Push', filmBurn: 'Film Burn', circularIris: 'Circular Iris', irisClose: 'Iris Close',
}

const Section = ({ title, children, right }) => (
  <div className="bg-gray-50 p-3 rounded-lg">
    <div className="flex items-center justify-between mb-2">
      <h4 className="text-sm font-medium text-gray-700">{title}</h4>
      {right}
    </div>
    {children}
  </div>
)

const Slider = ({ label, value, fallback, min, max, step, onChange, suffix }) => (
  <div>
    <label className="text-[11px] text-gray-500 flex justify-between mb-0.5">
      <span>{label}</span><span>{(value ?? fallback)}{suffix || ''}</span>
    </label>
    <input type="range" value={value ?? fallback} min={min} max={max} step={step}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
  </div>
)

export default function ImageEffectsPanel({ element, selectedElement, updateElementProperty }) {
  if (!element || element.type !== 'image') return null
  const set = (key, value) => updateElementProperty(selectedElement, key, value)
  const setGrade = (patch) => updateElementProperty(selectedElement, 'colorGrade', { ...(element.colorGrade || {}), ...patch })

  const grade = element.colorGrade || {}
  const motion = element.motion || {}
  const transition = element.transition || {}
  const camType = CAMERA_PRESETS.some(([v]) => v === motion.type) ? motion.type : ''

  // Transition shorthand: we expose a single type + duration applied to both in/out.
  const trType = transition.type || transition.in?.type || transition.out?.type || ''
  const trDur = transition.duration ?? transition.in?.duration ?? transition.out?.duration ?? 0.6

  return (
    <div className="space-y-3">
      {/* Color grade */}
      <Section title="Color Grade" right={
        (grade.preset || Object.keys(grade).length) ? (
          <button onClick={() => set('colorGrade', undefined)} className="text-xs text-red-500 hover:text-red-700">Reset</button>
        ) : null
      }>
        <div className="space-y-2">
          <select value={grade.preset || 'none'} onChange={(e) => setGrade({ preset: e.target.value === 'none' ? undefined : e.target.value })}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            {GRADE_PRESETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Slider label="Exposure" value={grade.exposure} fallback={0} min={-100} max={100} step={1} onChange={(v) => setGrade({ exposure: v })} />
          <Slider label="Brightness" value={grade.brightness} fallback={0} min={-100} max={100} step={1} onChange={(v) => setGrade({ brightness: v })} />
          <Slider label="Contrast" value={grade.contrast} fallback={0} min={-100} max={100} step={1} onChange={(v) => setGrade({ contrast: v })} />
          <Slider label="Saturation" value={grade.saturation} fallback={0} min={-100} max={100} step={1} onChange={(v) => setGrade({ saturation: v })} />
          <Slider label="Vibrance" value={grade.vibrance} fallback={0} min={-100} max={100} step={1} onChange={(v) => setGrade({ vibrance: v })} />
          <Slider label="Temperature" value={grade.temperature} fallback={0} min={-100} max={100} step={1} onChange={(v) => setGrade({ temperature: v })} />
          <Slider label="Tint" value={grade.tint} fallback={0} min={-100} max={100} step={1} onChange={(v) => setGrade({ tint: v })} />
          <Slider label="Hue" value={grade.hue} fallback={0} min={-180} max={180} step={1} suffix="°" onChange={(v) => setGrade({ hue: v })} />
        </div>
      </Section>

      {/* Cinematic camera motion (Ken Burns) */}
      <Section title="Camera Motion" right={
        camType ? <button onClick={() => set('motion', undefined)} className="text-xs text-red-500 hover:text-red-700">Clear</button> : null
      }>
        <div className="space-y-2">
          <select value={camType} onChange={(e) => {
            const t = e.target.value
            if (!t) { set('motion', undefined); return }
            set('motion', { type: t, duration: motion.duration || (element.time?.duration || 3) })
          }} className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            {CAMERA_PRESETS.map(([v, l]) => <option key={v || 'none'} value={v}>{l}</option>)}
          </select>
          {camType && (camType === 'kenBurnsZoomIn' || camType === 'kenBurnsZoomOut' || camType === 'cameraMove') && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-gray-500 block mb-0.5">Focus X</label>
                <input type="number" value={motion.focusX ?? motion.endFocusX ?? 0.5} min={0} max={1} step={0.05}
                  onChange={(e) => set('motion', { ...motion, focusX: parseFloat(e.target.value) })}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 block mb-0.5">Focus Y</label>
                <input type="number" value={motion.focusY ?? motion.endFocusY ?? 0.5} min={0} max={1} step={0.05}
                  onChange={(e) => set('motion', { ...motion, focusY: parseFloat(e.target.value) })}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
              </div>
            </div>
          )}
          {camType && (
            <p className="text-[11px] text-gray-400">
              Zoom/pan stays within the image bounds (no empty edges). Fine-tune via the Motion panel.
            </p>
          )}
        </div>
      </Section>

      {/* Clip transition */}
      <Section title="Transition" right={
        trType ? <button onClick={() => set('transition', undefined)} className="text-xs text-red-500 hover:text-red-700">Clear</button> : null
      }>
        <div className="space-y-2">
          <select value={trType} onChange={(e) => {
            const t = e.target.value
            if (!t) { set('transition', undefined); return }
            set('transition', { type: t, duration: trDur })
          }} className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">None</option>
            {TRANSITION_TYPES.map((t) => <option key={t} value={t}>{TRANSITION_LABELS[t] || t}</option>)}
          </select>
          {trType && (
            <>
              <div>
                <label className="text-[11px] text-gray-500 block mb-0.5">Duration (s)</label>
                <input type="number" value={trDur} min={0.1} max={5} step={0.1}
                  onChange={(e) => set('transition', { type: trType, duration: parseFloat(e.target.value) })}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
              </div>
              <p className="text-[11px] text-gray-400">
                Plays on this clip's entrance and exit. Overlap two clips on the timeline for a clip-to-clip transition.
              </p>
            </>
          )}
        </div>
      </Section>
    </div>
  )
}
